import { useState } from 'react';
import { isComplete, serviceClass } from '../../data/domain';
import { useJobs } from '../../data/hooks';
import { atNoon, dateKey, plural } from '../../lib/format';
import { Icon } from '../../ui/Icon';
import { useUI } from '../../ui/UIProvider';
import { PageHeading, View } from '../../ui/View';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const VISIBLE_JOBS = 3;

export function ScheduleView() {
  const jobs = useJobs();
  const ui = useUI();
  const requested = new URLSearchParams(window.location.search).get('date');
  const [selected, setSelected] = useState(() => (requested && /^\d{4}-\d{2}-\d{2}$/.test(requested) ? requested : dateKey()));
  const [month, setMonth] = useState(() => { const date = atNoon(selected); return { year: date.getFullYear(), month: date.getMonth() }; });
  const today = dateKey();

  const firstDay = (new Date(month.year, month.month, 1).getDay() + 6) % 7;
  const cells = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(month.year, month.month, index - firstDay + 1, 12);
    return { date, key: dateKey(date), muted: date.getMonth() !== month.month };
  });
  const shift = (delta: number) => setMonth((current) => { const date = new Date(current.year, current.month + delta, 1); return { year: date.getFullYear(), month: date.getMonth() }; });
  const goToday = () => { const date = atNoon(today); setSelected(today); setMonth({ year: date.getFullYear(), month: date.getMonth() }); };

  function openDay(key: string) {
    setSelected(key);
    const date = atNoon(key);
    setMonth({ year: date.getFullYear(), month: date.getMonth() });
    ui.open('daySchedule', { date: key });
  }

  return (
    <View id="schedule">
      <PageHeading eyebrow="Field operations" title="Service schedule" subtitle="Select a date to open the hourly day board, or add a new job.">
        <button className="btn btn-primary" id="calendarNewJob" type="button" onClick={() => ui.open('jobEditor', { mode: 'new', date: selected })}><Icon name="plus" size={16} />New job</button>
      </PageHeading>
      <section className="panel calendar" aria-labelledby="calendarTitle">
        <header className="calendar-toolbar">
          <h2 id="calendarTitle">{new Date(month.year, month.month).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}</h2>
          <div className="button-group">
            <button className="btn btn-secondary btn-sm" type="button" onClick={goToday}>Today</button>
            <button className="icon-button" id="previousMonth" aria-label="Previous month" onClick={() => shift(-1)}><Icon name="chevronLeft" /></button>
            <button className="icon-button" id="nextMonth" aria-label="Next month" onClick={() => shift(1)}><Icon name="chevronRight" /></button>
          </div>
        </header>
        <div className="calendar-weekdays" aria-hidden="true">{WEEKDAYS.map((day) => <span key={day}>{day}</span>)}</div>
        <div className="calendar-days" id="calendarDays">
          {cells.map(({ date, key, muted }) => {
            const dayJobs = jobs.filter((job) => job.date === key).sort((a, b) => a.startTime.localeCompare(b.startTime));
            const done = dayJobs.filter(isComplete).length;
            const classes = ['calendar-day', muted && 'muted', key === selected && 'selected', key === today && 'today'].filter(Boolean).join(' ');
            return (
              <button key={key} className={classes} data-date={key} onClick={() => openDay(key)} aria-label={`${date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}: ${plural(dayJobs.length, 'job')}${done ? `, ${done} done` : ''}`}>
                <span className="day-head">
                  <span className="day-number">{date.getDate()}</span>
                  {dayJobs.length > 0 && <span className="day-count">{dayJobs.length}</span>}
                </span>
                {dayJobs.length > 0 && (
                  <span className="calendar-job-list">
                    {dayJobs.slice(0, VISIBLE_JOBS).map((job) => <span key={job.id} className={`cal-event ${serviceClass(job)}${isComplete(job) ? ' completed' : ''}${job.cancelled ? ' cancelled' : ''}`}>{job.serviceCode}</span>)}
                    {dayJobs.length > VISIBLE_JOBS && <span className="cal-more">+{dayJobs.length - VISIBLE_JOBS} more</span>}
                  </span>
                )}
                {dayJobs.length > 0 && <span className="calendar-dots" aria-hidden="true">{dayJobs.slice(0, 4).map((job) => <i key={job.id} className={`${serviceClass(job)}${isComplete(job) ? ' completed' : ''}`} />)}</span>}
              </button>
            );
          })}
        </div>
        <footer className="calendar-legend" aria-label="Legend">
          <span><i className="general" />General pest</span>
          <span><i className="rodent" />Rodent</span>
          <span><i className="termite" />Termite</span>
          <span><i className="general completed" />Completed</span>
        </footer>
      </section>
    </View>
  );
}
